import { Alert, Box, Button, CircularProgress, Tab, Tabs, Typography } from "@mui/material";
import { useState } from "react";
import { Title, useCanAccessResources, useGetIdentity, useTranslate } from "react-admin";
import { Link as RouterLink, Navigate, Route, Routes, useParams } from "react-router-dom";

import type { PortalResource } from "../auth/access";
import type { UserIdentity } from "../auth/lib";
import { OrganizationAccountLink } from "../components/OrganizationAccountLink";
import { OrganizationBillingManagement } from "../components/OrganizationBillingManagement";
import { OrganizationBillingSection } from "../components/OrganizationBillingSection";
import { RequirePermission } from "../components/RequirePermission";
import { ResourceName } from "../constants/resources";
import { useCustomerServiceLearningOrganizations } from "../hooks/useCustomerServiceLearningOrganizations";
import { useOrganizationAccount } from "../hooks/useOrganizationAccount";
import {
  CUSTOMER_SERVICE_LEARNING_GRANTS_SEGMENT,
  CUSTOMER_SERVICE_ORGANIZATIONS_ROUTE,
  customerServiceLearningGrantsPath,
  customerServiceOrganizationPath,
  isCustomerServiceLearningGrantsSubPath,
} from "../lib/customerServiceOrganizationRoutes";
import { OrganizationLearningGrantsPage } from "./OrganizationLearningGrantsPage";

type SelectedOrganizationContentProps = {
  identityId: string;
  organizationId: string;
  organizationName: string;
};

const SelectedOrganizationContent = ({
  identityId,
  organizationId,
  organizationName,
}: SelectedOrganizationContentProps) => {
  const [billingRevision, setBillingRevision] = useState(0);
  const contentKey = `${identityId}:${organizationId}`;

  return (
    <Box key={contentKey}>
      <OrganizationBillingManagement
        organizationId={organizationId}
        organizationName={organizationName}
        onAccountChanged={() => setBillingRevision(value => value + 1)}
      />
      <OrganizationBillingSection key={`${contentKey}:${billingRevision}`} organizationId={organizationId} />
    </Box>
  );
};

type OrganizationTab = "billing" | "learningGrants";

const ORGANIZATION_TAB_RESOURCES: PortalResource[] = [ResourceName.OrganizationBilling, ResourceName.LearningGrant];

type CustomerServiceOrganizationOverviewProps = {
  identity: UserIdentity;
  organizationId: string;
};

const CustomerServiceOrganizationOverview = ({
  identity,
  organizationId,
}: CustomerServiceOrganizationOverviewProps) => {
  const translate = useTranslate();
  const { "*": subPath } = useParams();
  const { organizations, isLoading, error, reload } = useCustomerServiceLearningOrganizations();
  const { canAccess, isPending: isAccessPending } = useCanAccessResources({
    action: "list",
    resources: ORGANIZATION_TAB_RESOURCES,
  });
  const canManageBilling = canAccess?.[ResourceName.OrganizationBilling] === true;
  const canManageLearningGrants = canAccess?.[ResourceName.LearningGrant] === true;
  const organization = organizations.find(candidate => candidate.id === organizationId);
  const account = useOrganizationAccount(organizationId);

  if (isLoading || isAccessPending) {
    return (
      <Box sx={{ display: "grid", minHeight: 160, placeItems: "center" }}>
        <CircularProgress aria-label={translate("customerServiceOrganizations.loading")} />
      </Box>
    );
  }
  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" onClick={() => void reload()}>
            {translate("billingAccess.retry")}
          </Button>
        }
      >
        {translate("customerServiceOrganizations.loadError")}
      </Alert>
    );
  }
  if (!organization) {
    return (
      <Alert
        severity="info"
        action={
          <Button color="inherit" component={RouterLink} to={CUSTOMER_SERVICE_ORGANIZATIONS_ROUTE}>
            {translate("customerServiceOrganizations.backToList")}
          </Button>
        }
      >
        {translate("customerServiceOrganizations.notFound")}
      </Alert>
    );
  }

  const organizationName = organization.name || organization.id;
  const activeTab: OrganizationTab = isCustomerServiceLearningGrantsSubPath(subPath) ? "learningGrants" : "billing";

  return (
    <Box data-testid="customer-service-organization-overview">
      <Title title={organizationName} />
      <Typography component="h1" variant="h4" mb={account ? 0.5 : 2}>
        {organizationName}
      </Typography>
      {account && (
        <Typography variant="body2" mb={2} data-testid="customer-service-organization-account">
          {translate("customerServiceOrganizations.account.label")}: <OrganizationAccountLink {...account} />
        </Typography>
      )}
      <Tabs
        value={(activeTab === "learningGrants" ? canManageLearningGrants : canManageBilling) ? activeTab : false}
        aria-label={translate("customerServiceOrganizations.tabs.label")}
        sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
      >
        {canManageBilling && (
          <Tab
            value="billing"
            label={translate("customerServiceOrganizations.tabs.billing")}
            component={RouterLink}
            to={customerServiceOrganizationPath(organization.id)}
          />
        )}
        {canManageLearningGrants && (
          <Tab
            value="learningGrants"
            label={translate("resources.LearningGrant")}
            component={RouterLink}
            to={customerServiceLearningGrantsPath(organization.id)}
          />
        )}
      </Tabs>
      <Routes>
        <Route
          index
          element={
            canManageBilling ? (
              <SelectedOrganizationContent
                identityId={identity.id}
                organizationId={organization.id}
                organizationName={organizationName}
              />
            ) : (
              <Navigate to={customerServiceLearningGrantsPath(organization.id)} replace />
            )
          }
        />
        <Route
          path={CUSTOMER_SERVICE_LEARNING_GRANTS_SEGMENT}
          element={
            <RequirePermission permission={ResourceName.LearningGrant}>
              <OrganizationLearningGrantsPage organizationId={organization.id} organizationName={organizationName} />
            </RequirePermission>
          }
        />
        <Route path="*" element={<Navigate to={customerServiceOrganizationPath(organization.id)} replace />} />
      </Routes>
    </Box>
  );
};

export const CustomerServiceOrganizationPage = () => {
  const { data, isPending } = useGetIdentity();
  const { organizationId } = useParams<{ organizationId: string }>();
  const identity = data as UserIdentity | undefined;

  if (isPending) return null;
  // The route checks the facilities access (App.tsx).
  if (!identity || !organizationId) return <Navigate to="/" replace />;
  return (
    <CustomerServiceOrganizationOverview key={organizationId} identity={identity} organizationId={organizationId} />
  );
};
